import { Navigate } from "@ngxs/router-plugin";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  Auth,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from "@angular/fire/auth";
import { State, Action, StateContext, Selector, Store } from "@ngxs/store";
import { Subscription } from "rxjs";
import { User } from "../interface/user";
import {
  SignOut,
  UpdateUserSession,
  WatchIdToken,
  Login,
  ResetPasswordEmail,
  SetDarkModePreference,
} from "./actions/user.actions";
import { Injectable, inject } from "@angular/core";
import { UsersService } from "../firestore/users.service";
import { friendlyFirebaseAuthMessage } from "../utils/firebase-auth-errors";

@Injectable()
@State<User>({
  name: "user",
  defaults: {
    isGhost: "1",
  },
})
export class UserState {
  installPrompt: any;

  /**
   * Live subscription to the current user's `users/{uid}` doc. Created
   * once per signed-in session and torn down on sign-out, so token refreshes
   * (which fire `onIdTokenChanged` every hour) don't trigger extra reads.
   */
  private profileSub?: Subscription;
  private currentUid: string | null = null;

  @Selector()
  static me({ firebaseUser }: User) {
    return firebaseUser?.AppUser ?? null;
  }

  @Selector()
  static token({ token }: User) {
    return token;
  }

  @Selector()
  static isGhost(state: User) {
    return state.isGhost;
  }

  @Selector()
  static firebaseUser({ firebaseUser }: User) {
    return firebaseUser ?? null;
  }

  @Selector()
  static UID({ firebaseUser }: User) {
    return firebaseUser?.UID ?? null;
  }

  private auth: Auth = inject(Auth);
  private users: UsersService = inject(UsersService);

  constructor(private store: Store, private snackBar: MatSnackBar) {}

  ngxsOnInit({ dispatch }: StateContext<User>) {
    this.auth.useDeviceLanguage();
    dispatch(new WatchIdToken());
  }

  openSnackBar = (
    message: string,
    button: string,
    selectedClass: string,
    duration: number
  ) => {
    this.snackBar.open(message, button, {
      duration,
      panelClass: [selectedClass],
    });
  };

  @Action(UpdateUserSession)
  UpdateUserSession(
    { patchState }: StateContext<User>,
    { info }: UpdateUserSession
  ) {
    const { firebaseUser, token, isGhost } = info;
    patchState({ firebaseUser, token, isGhost });
  }

  /**
   * Subscribes to Firebase Auth changes. We use `onIdTokenChanged` (instead
   * of `onAuthStateChanged`) so that hourly token refreshes update only the
   * cached token without re-fetching the user profile.
   *
   * The user profile itself is mirrored from a single live subscription
   * created when the uid changes — so navigating between routes never
   * spawns a new `getDoc` call.
   */
  @Action(WatchIdToken)
  watchIdToken({ patchState, dispatch, getState: _getState }: StateContext<User>) {
    this.auth.useDeviceLanguage();

    return this.auth.onIdTokenChanged(async (user) => {
      const path =
        typeof window !== "undefined" ? window.location.pathname : "/";

      if (!user) {
        this.tearDownProfileSubscription();
        patchState({ firebaseUser: null, token: null, isGhost: "1" });
        const protectedRoute =
          path.startsWith("/cms") || path.startsWith("/profile");
        if (protectedRoute) dispatch(new Navigate(["/home"]));
        return;
      }

      const token = await user.getIdToken();
      const prevUid = this.currentUid;

      if (user.isAnonymous) {
        if (!path.startsWith("/home")) dispatch(new Navigate(["/home"]));
        patchState({
          firebaseUser: { UID: user.uid, id: user.uid, AppUser: null },
          token,
          isGhost: "1",
        });
        return;
      }

      // Token refresh for the same user — keep the live profile subscription
      // alive and just update the cached token. No Firestore traffic.
      if (prevUid === user.uid) {
        patchState({ token });
        return;
      }

      this.tearDownProfileSubscription();
      this.currentUid = user.uid;
      this.profileSub = this.users.user$(user.uid).subscribe({
        next: (appUser) => {
          patchState({
            firebaseUser: { UID: user.uid, id: user.uid, AppUser: appUser },
            token,
            isGhost: "0",
          });
        },
        error: (err: any) => {
          this.openSnackBar(
            `No se pudo cargar el perfil: ${err?.message ?? err}`,
            "OK",
            "custom-snackbar-error",
            3000
          );
        },
      });

      const isPublicRoute = path === "/" || path.startsWith("/home");
      if (isPublicRoute) {
        dispatch(new Navigate(["/cms/inicio"]));
      }
    });
  }

  private tearDownProfileSubscription() {
    this.profileSub?.unsubscribe();
    this.profileSub = undefined;
    this.currentUid = null;
  }

  @Action(SignOut)
  signOut({ patchState, dispatch }: StateContext<User>) {
    return this.auth.signOut().then(() => {
      this.tearDownProfileSubscription();
      patchState({
        firebaseUser: null,
        isGhost: "1",
        token: null,
      });
      dispatch(new Navigate(["home"]));
    });
  }

  @Action(Login)
  async login({}: StateContext<User>, { credentials }: Login) {
    // The auth-state watcher (WatchIdToken) takes care of loading the
    // profile and redirecting once Firebase emits the new user, so we just
    // sign in here and let the side-effects flow. We notify the user on
    // failure AND rethrow so the dispatching modal can stay open with the
    // form intact (instead of closing on a wrong password).
    try {
      await signInWithEmailAndPassword(
        this.auth,
        credentials.email,
        credentials.password
      );
    } catch (err: unknown) {
      const msg = friendlyFirebaseAuthMessage(err);
      this.snackBar.open(msg, "OK", {
        duration: 5200,
        panelClass: ["custom-snackbar-error"],
      });
      throw err;
    }
  }

  @Action(ResetPasswordEmail)
  ResetPasswordEmail({}: StateContext<User>, { email }: ResetPasswordEmail) {
    sendPasswordResetEmail(this.auth, email)
      .then(() => {
        this.openSnackBar(
          "Email enviado con exito",
          "OK",
          "custom-snackbar",
          3000
        );
      })
      .catch(() => {
        this.openSnackBar(
          "Error enviar el email",
          "OK",
          "custom-snackbar-error",
          3000
        );
      });
  }

  @Action(SetDarkModePreference)
  async setDarkModePreference(
    { getState, patchState: _patchState }: StateContext<User>,
    { darkMode }: SetDarkModePreference
  ) {
    const state = getState();
    const uid = state.firebaseUser?.UID;
    if (!uid) return;

    // mergeUserDoc never reads — Firestore takes care of preserving the
    // other fields via `merge: true`. The live `user$()` subscription will
    // re-emit with the updated value automatically.
    await this.users.mergeUserDoc(uid, { darkMode });
  }
}
